
import { useEffect } from 'react';
import type { Word } from '@/lib/game-utils';
import { WordRow } from './components/WordRow';
import { SimpleWordList } from './components/SimpleWordList';
import { TooltipHint } from './components/TooltipHint';
import { SolveTimeDisplay } from './components/SolveTimeDisplay';
import { useWordLinks } from './hooks/useWordLinks';
import { useSavePlayerRanking } from './hooks/useSavePlayerRanking';

interface OrderComparisonProps {
  selectedWords: Array<{
    word: Word | string;
    order: number;
  }>;
  targetOrder: Array<Word | string>;
  onMatchCalculated?: (percentage: number) => void;
  solveTime?: number | null;
  wordLinks?: string[];
}

export function OrderComparison({
  selectedWords,
  targetOrder,
  onMatchCalculated,
  solveTime,
  wordLinks = []
}: OrderComparisonProps) {
  // Get words in the selected order
  const orderedSelectedWords = [...selectedWords]
    .sort((a, b) => a.order - b.order)
    .map(item => item.word);

  // Use hooks for managing links and saving player rankings
  const { highlightedLinks, showTooltipImage, clickedLinks, handleLinkClick } = 
    useWordLinks(targetOrder.length);
  
  // Hook to save player ranking silently
  useSavePlayerRanking(orderedSelectedWords);

  // Calculate match percentage
  useEffect(() => {
    if (onMatchCalculated) {
      const matchCount = orderedSelectedWords.filter(
        (word, index) => String(targetOrder[index]) === String(word)
      ).length;
      const matchPercentage = matchCount / targetOrder.length * 100;
      onMatchCalculated(matchPercentage);

      // Log for debugging
      console.log("Match calculation:", {
        orderedSelectedWords,
        targetOrder,
        matchCount,
        matchPercentage,
        wordLinks
      });
    }
  }, [orderedSelectedWords, targetOrder, onMatchCalculated, wordLinks]);

  return (
    <div className="space-y-2">
      {/* Column headers - Only show when comparing with target order */}
      {targetOrder && targetOrder.length > 0 && (
        <div className="flex justify-center px-2 gap-6">
          <div className="text-gray-600 font-medium text-sm w-[40%] text-center">YOU</div>
          <div className="text-gray-600 font-medium text-sm w-[40%] text-center relative">
            <span>MML Team</span>
            {/* Show tooltip image when comparing with game order and links */}
            {showTooltipImage && wordLinks && wordLinks.length > 0 && (
              <TooltipHint showTooltip={showTooltipImage} />
            )}
          </div>
        </div>
      )}
      
      {/* Word display rows */}
      {targetOrder && targetOrder.length > 0 ? (
        orderedSelectedWords.map((word, index) => {
          const isMatch = String(word) === String(targetOrder[index]);
          return (
            <WordRow
              key={`word-${index}`}
              userWord={word}
              targetWord={targetOrder[index]}
              isMatch={isMatch}
              wordLink={wordLinks[index]}
              isHighlighted={highlightedLinks[index]}
              isClicked={clickedLinks[index]}
              onLinkClick={() => handleLinkClick(index)}
            />
          );
        })
      ) : (
        // Simple display for player card view (no comparison)
        <SimpleWordList words={orderedSelectedWords} />
      )}
      
      {/* Display solve time if available */}
      {solveTime !== null && solveTime !== undefined && targetOrder && targetOrder.length > 0 && (
        <SolveTimeDisplay solveTime={solveTime} />
      )}
    </div>
  );
}
